import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { channelControl, draftKey, useCountdown, type PreferenceDraft } from '../account/helpers';
import type {
  AccountResponse, InvitationIssuedResponse, NotificationChannel, NotificationPreferenceResponse, SetPreferenceRequest,
} from '../api/types';
import { useApi } from '../auth/ApiProvider';
import { useSession } from '../auth/SessionProvider';
import { normalisePhone } from '../format';
import { useI18n } from '../i18n/I18nProvider';
import { CodeField, CodeWait, ResendTimer } from './AuthFlows';
import { CurrentPasswordField, EmailInput, Field, Input, NewPasswordFields, PhoneInput } from './Field';
import { Badge, Button, Card, Notice, PageHeader, Row, Stack } from './primitives';
import { LegalFooter } from './Legal';
import { PasskeysCard } from './Passkeys';
import { PushCard } from './PushCard';
import { PhotoPicker } from './Photo';
import { ErrorState, LoadingState } from './states';

// ---- The invitation panel -------------------------------------------------------------------------------

/**
 * What the person who sent an invitation sees straight afterwards (decision 2026-09-23 #3).
 *
 * The link is shown once, with a copy button, because the text message may be fake, slow or refused, and
 * the inviter is the fallback. It is safe to show: accepting it also needs a code texted to the invitee's
 * own phone. Used by all four invite flows and every resend.
 */
export function InvitationIssuedPanel({ invitation, onDismiss }: {
  invitation: InvitationIssuedResponse;
  onDismiss?: () => void;
}) {
  const { t, formatDate, formatPhone } = useI18n();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(invitation.link);
      setCopied(true);
    } catch {
      // Clipboard access can be refused (an insecure origin, a denied permission). The link is in a
      // read-only field the person can select by hand, so a refusal changes nothing they cannot do.
      setCopied(false);
    }
  }

  const sent = invitation.sms === 'sent';

  return (
    <Notice tone={sent ? 'success' : 'warning'} title={t('invitation.panel.title')}>
      <Stack tight>
        <p>
          {sent
            ? t('invitation.panel.smsSent', { phone: formatPhone(invitation.phone) })
            : t('invitation.panel.smsFailed')}
        </p>

        <Field label={t('invitation.panel.link')}>
          {(props) => (
            <Input
              {...props}
              readOnly
              dir="ltr"
              value={invitation.link}
              onFocus={(event) => event.currentTarget.select()}
            />
          )}
        </Field>

        <Row>
          <Button variant="secondary" onClick={() => void copy()}>
            {copied ? t('action.copied') : t('invitation.panel.copy')}
          </Button>
          {onDismiss && <Button variant="ghost" onClick={onDismiss}>{t('action.close')}</Button>}
        </Row>

        <p className="dg-muted dg-text-sm">{t('invitation.panel.expires', { date: formatDate(invitation.expiresAt) })}</p>
        <p className="dg-muted dg-text-sm">{t('invitation.panel.once')}</p>
      </Stack>
    </Notice>
  );
}

// ---- Notification channels ------------------------------------------------------------------------------

const CHANNELS: NotificationChannel[] = ['inApp', 'email', 'sms', 'push'];

/**
 * Which notifications arrive on which channel (FR-NOT-06).
 *
 * One row per kind of notification, one column per channel the person can receive anything on. The
 * safety alerts' in-app box is ticked and locked rather than hidden, so a family can see those alerts are
 * guaranteed. Shared by the notification centre and the account screen.
 */
export function NotificationPreferencesGrid() {
  const api = useApi();
  const { t, has } = useI18n();
  // In the screen's language; the server's description is English, and only the fallback for a type added since.
  const typeLabel = (preference: NotificationPreferenceResponse) => {
    const key = `notifications.type.${preference.code}`;
    return has(key) ? t(key) : (preference.description ?? preference.code);
  };
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<PreferenceDraft>({});

  const preferences = useQuery({
    queryKey: ['notifications', 'preferences'],
    queryFn: () => api.notifications.preferences(),
  });

  const save = useMutation({
    mutationFn: () => api.notifications.setPreferences(changes(preferences.data ?? [], draft)),
    onSuccess: async () => {
      setDraft({});
      await queryClient.invalidateQueries({ queryKey: ['notifications', 'preferences'] });
    },
  });

  if (preferences.isPending) return <LoadingState />;
  if (preferences.isError) {
    return <ErrorState error={preferences.error} onRetry={() => void preferences.refetch()} />;
  }

  // Push reaches the guardian app alone (Q68): the staff apps have no subscription to switch.
  const columns = CHANNELS.filter((channel) =>
    (channel !== 'push' || api.appName === 'guardian')
    && preferences.data.some((p) => p.channels.some((c) => c.channel === channel)));

  return (
    <Stack>
      {api.appName === 'guardian' && <PushCard />}
      {save.isError && <ErrorState error={save.error} />}
      {columns.length > 1 && <p className="dg-muted dg-text-sm">{t('notifications.channels.hint')}</p>}

      <div className="dg-table-wrap">
        <table className="dg-table dg-table--preferences">
          <thead>
            <tr>
              <th scope="col">{t('notifications.title')}</th>
              {columns.map((channel) => (
                <th key={channel} scope="col">{t(`notifications.channel.${channel}`)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preferences.data.map((preference) => (
              <tr key={preference.notificationTypeId}>
                <th scope="row">
                  <span className="dg-stack dg-stack--tight">
                    <span>{typeLabel(preference)}</span>
                    <span className="dg-row">
                      {preference.isMandatory && <Badge tone="danger">{t('notifications.mandatory')}</Badge>}
                      {preference.isDigestible && <Badge tone="neutral">{t('notifications.digested')}</Badge>}
                    </span>
                  </span>
                </th>
                {columns.map((channel) => {
                  const control = channelControl(preference, channel, draft);
                  if (!control.offered) return <td key={channel} aria-hidden="true">—</td>;

                  const label = t('notifications.cell', {
                    type: typeLabel(preference),
                    channel: t(`notifications.channel.${channel}`),
                  });
                  return (
                    <td key={channel}>
                      <input
                        type="checkbox"
                        aria-label={label}
                        title={control.disabled && channel === 'inApp' ? t('notifications.locked') : undefined}
                        checked={control.checked}
                        // Refused by the server too; disabling it here only spares the round trip.
                        disabled={control.disabled}
                        onChange={(event) => setDraft((current) => ({
                          ...current,
                          [draftKey(preference.notificationTypeId, channel)]: event.target.checked,
                        }))}
                      />
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Row>
        <Button
          busy={save.isPending}
          busyLabel={t('action.saving')}
          disabled={Object.keys(draft).length === 0}
          onClick={() => save.mutate()}
        >
          {t('action.save')}
        </Button>
      </Row>
    </Stack>
  );
}

/** The entries to send for a draft: only what changed, and never a locked channel. */
export function changes(preferences: NotificationPreferenceResponse[], draft: PreferenceDraft): SetPreferenceRequest[] {
  const result: SetPreferenceRequest[] = [];
  for (const preference of preferences) {
    for (const entry of preference.channels) {
      const wanted = draft[draftKey(preference.notificationTypeId, entry.channel)];
      if (wanted === undefined || entry.locked || wanted === entry.enabled) continue;
      result.push({
        notificationTypeId: preference.notificationTypeId,
        channel: entry.channel,
        enabled: wanted,
        quietHoursStart: preference.quietHoursStart,
        quietHoursEnd: preference.quietHoursEnd,
      });
    }
  }
  return result;
}

// ---- The account screen ---------------------------------------------------------------------------------

/**
 * "My account", the same in all four apps (decisions 2026-09-23 #12, #13 and #15).
 *
 * Every change asks for the current password. Notification preferences belong to a tenant membership,
 * so the card is shown only to someone signed in to a tenant: platform staff hold no such membership.
 */
export function AccountScreen() {
  const { t } = useI18n();
  const { session } = useSession();
  const notifications = session?.membership.scope === 'Tenant';

  const account = useAccount();

  if (account.isPending) return <LoadingState />;
  if (account.isError) return <ErrorState error={account.error} onRetry={() => void account.refetch()} />;

  return (
    <Stack>
      <PageHeader title={t('account.title')} />
      <PhotoCard />
      <ProfileCard />
      <EmailCard />
      <PasswordCard />
      <PasskeysCard />
      <SessionsCard />
      {notifications && (
        <Card title={t('account.notifications')}>
          <NotificationPreferencesGrid />
        </Card>
      )}
      <Card title={t('legal.title')}>
        <LegalFooter />
      </Card>
    </Stack>
  );
}

/** The caller's own account. Shared with the header, which shows the photo from the same query. */
export function useAccount({ enabled = true }: { enabled?: boolean } = {}) {
  const api = useApi();
  return useQuery({ queryKey: ['account'], queryFn: () => api.account.get(), enabled });
}

function PhotoCard() {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const account = useAccount();

  if (!account.data) return null;

  // Each answer is the whole account with a fresh signed link, so the header's photo changes with it.
  const store = (result: AccountResponse) => queryClient.setQueryData(['account'], result);

  return (
    <Card title={t('account.photo')}>
      <PhotoPicker
        name={account.data.fullName}
        photoUrl={account.data.photoUrl}
        hint={t('account.photo.hint')}
        onSave={(photo) => api.account.setPhoto(photo).then(store)}
        onRemove={() => api.account.removePhoto().then(store)}
      />
    </Card>
  );
}

function ProfileCard() {
  const api = useApi();
  const { t, formatPhone, formatDateTime } = useI18n();
  const queryClient = useQueryClient();
  const account = useAccount();
  const [editing, setEditing] = useState(false);
  const [newPhone, setNewPhone] = useState('');
  const [password, setPassword] = useState('');
  const [phoneError, setPhoneError] = useState<string | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const request = useMutation({
    mutationFn: (phone: string) => api.account.requestPhoneChange(phone, password),
    onSuccess: async (result) => {
      setSentTo(result.maskedNewPhone);
      setEditing(false);
      setNewPhone('');
      await queryClient.invalidateQueries({ queryKey: ['account'], exact: true });
    },
    onSettled: () => setPassword(''),
  });

  const cancel = useMutation({
    mutationFn: () => api.account.cancelPhoneChange(),
    onSuccess: async () => {
      setSentTo(null);
      await queryClient.invalidateQueries({ queryKey: ['account'], exact: true });
    },
  });

  if (!account.data) return null;
  const { fullName, phone, phoneConfirmed, pendingPhoneChange } = account.data;

  function submit(event: FormEvent) {
    event.preventDefault();
    const normalised = normalisePhone(newPhone);
    setPhoneError(normalised ? null : t('auth.phone.required'));
    if (normalised) request.mutate(normalised);
  }

  return (
    <Card title={t('account.profile')}>
      <Stack>
        <div className="dg-stack dg-stack--tight">
          <span className="dg-field__label">{t('account.name')}</span>
          <span>{fullName ?? '—'}</span>
          <span className="dg-muted dg-text-sm">{t('account.name.hint')}</span>
        </div>

        <Row between>
          <span className="dg-stack dg-stack--tight">
            <span className="dg-field__label">{t('account.phone')}</span>
            <span className="dg-row">
              <span>{formatPhone(phone)}</span>
              {!phoneConfirmed && <Badge tone="warning">{t('account.phone.unconfirmed')}</Badge>}
            </span>
            <span className="dg-muted dg-text-sm">{t('account.phone.hint')}</span>
          </span>
          {!editing && <Button variant="secondary" onClick={() => setEditing(true)}>{t('account.change')}</Button>}
        </Row>

        {sentTo && <Notice tone="success">{t('account.phone.sent', { phone: formatPhone(sentTo) })}</Notice>}

        {pendingPhoneChange && (
          <Notice tone="info">
            <Stack tight>
              <span>{t('account.phone.pending', {
                phone: formatPhone(pendingPhoneChange.maskedNewPhone),
                time: formatDateTime(pendingPhoneChange.expiresAt),
              })}</span>
              {cancel.isError && <ErrorState error={cancel.error} />}
              <Row>
                <Button variant="ghost" busy={cancel.isPending} onClick={() => cancel.mutate()}>
                  {t('account.phone.cancel')}
                </Button>
              </Row>
            </Stack>
          </Notice>
        )}

        {editing && (
          <form onSubmit={submit} noValidate>
            <Stack>
              {request.isError && <ErrorState error={request.error} />}
              <Field label={t('account.phone.new')} hint={t('auth.phone.hint')} error={phoneError} required>
                {(props) => (
                  <PhoneInput {...props} value={newPhone} onChange={(event) => setNewPhone(event.target.value)} autoFocus />
                )}
              </Field>
              <AccountPasswordField value={password} onChange={setPassword} />
              <Row>
                <Button type="submit" busy={request.isPending} busyLabel={t('action.saving')}>
                  {t('account.phone.send')}
                </Button>
                <Button variant="ghost" onClick={() => setEditing(false)}>{t('action.cancel')}</Button>
              </Row>
            </Stack>
          </form>
        )}
      </Stack>
    </Card>
  );
}

function EmailCard() {
  const api = useApi();
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const account = useAccount();
  const [editing, setEditing] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [done, setDone] = useState<'saved' | 'removed' | null>(null);

  const save = useMutation({
    mutationFn: (value: string | null) => api.account.setEmail(value, password),
    onSuccess: (result, value) => {
      queryClient.setQueryData(['account'], result);
      setDone(value ? 'saved' : 'removed');
      setEditing(false);
    },
    onSettled: () => setPassword(''),
  });

  if (!account.data) return null;
  const current = account.data.email ?? null;

  function start() {
    setEmail(current ?? '');
    setDone(null);
    setEditing(true);
  }

  return (
    <Card title={t('account.email')} hint={t('account.email.hint')}>
      <Stack>
        <Row between>
          <span dir="ltr">{current ?? t('account.email.none')}</span>
          {!editing && <Button variant="secondary" onClick={start}>{t('account.change')}</Button>}
        </Row>

        {done && <Notice tone="success">{t(done === 'saved' ? 'account.email.saved' : 'account.email.removed')}</Notice>}

        {editing && (
          <form onSubmit={(event) => { event.preventDefault(); save.mutate(email.trim() || null); }} noValidate>
            <Stack>
              {save.isError && <ErrorState error={save.error} />}
              <Field label={t('account.email.new')}>
                {(props) => (
                  <EmailInput
                    {...props}
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                  />
                )}
              </Field>
              <AccountPasswordField value={password} onChange={setPassword} />
              <Row>
                <Button type="submit" busy={save.isPending} busyLabel={t('action.saving')}>
                  {t('account.email.save')}
                </Button>
                {current && (
                  <Button variant="danger" disabled={save.isPending} onClick={() => save.mutate(null)}>
                    {t('account.email.remove')}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => setEditing(false)}>{t('action.cancel')}</Button>
              </Row>
            </Stack>
          </form>
        )}
      </Stack>
    </Card>
  );
}

/**
 * Changes the password with the current one, or, for someone who has forgotten it, with a code texted to the
 * account's own number (Q25). Inside a session the code is enough and nothing waits: the session already proves
 * the account. An administrator or platform member must be in a passkey session, which the API says if not.
 */
export function PasswordCard() {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const [forgotten, setForgotten] = useState(false);
  const [saved, setSaved] = useState(false);

  async function done() {
    setForgotten(false);
    setSaved(true);
    await queryClient.invalidateQueries({ queryKey: ['account', 'sessions'] });
  }

  return (
    <Card title={t('account.password')}>
      <Stack>
        {saved && <Notice tone="success">{t('account.password.saved')}</Notice>}
        {forgotten
          ? <PasswordResetForm onDone={done} onCancel={() => setForgotten(false)} />
          : <PasswordChangeForm onDone={done} onForgotten={() => { setSaved(false); setForgotten(true); }} />}
      </Stack>
    </Card>
  );
}

function PasswordChangeForm({ onDone, onForgotten }: { onDone: () => Promise<void>; onForgotten: () => void }) {
  const api = useApi();
  const { t } = useI18n();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const change = useMutation({
    mutationFn: () => api.account.changePassword(current, next),
    onSuccess: async () => {
      setNext('');
      setConfirmation('');
      await onDone();
    },
    onSettled: () => setCurrent(''),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const differs = next !== confirmation;
    setMismatch(differs);
    if (!differs) change.mutate();
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        {change.isError && <ErrorState error={change.error} />}
        <AccountPasswordField value={current} onChange={setCurrent} />
        <NewPasswordFields
          labels={{
            password: t('account.password.new'),
            confirm: t('account.password.confirm'),
            mismatch: t('account.password.mismatch'),
          }}
          password={next}
          confirmation={confirmation}
          onPassword={setNext}
          onConfirmation={setConfirmation}
          mismatch={mismatch}
        />
        <Row>
          <Button type="submit" busy={change.isPending} busyLabel={t('action.saving')}>
            {t('account.password.save')}
          </Button>
          <Button variant="ghost" onClick={onForgotten}>{t('auth.forgotPassword')}</Button>
        </Row>
      </Stack>
    </form>
  );
}

/** A code to the account's own number, then the new password. Nothing is sent until the person asks. */
function PasswordResetForm({ onDone, onCancel }: { onDone: () => Promise<void>; onCancel: () => void }) {
  const api = useApi();
  const { t, formatPhone } = useI18n();
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [next, setNext] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [mismatch, setMismatch] = useState(false);

  const send = useMutation({
    mutationFn: () => api.account.sendSessionResetCode(),
    onSuccess: (result) => setSentTo(result.maskedPhone),
  });
  const { remaining: wait } = useCountdown(send.error);

  const reset = useMutation({
    mutationFn: () => api.account.resetPasswordInSession(code, next),
    onSuccess: async () => {
      setCode('');
      await onDone();
    },
    onSettled: () => { setNext(''); setConfirmation(''); },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    const missingCode = code.length !== 6;
    const differs = next !== confirmation;
    setCodeError(missingCode ? t('auth.code.required') : null);
    setMismatch(differs);
    if (!missingCode && !differs) reset.mutate();
  }

  if (!sentTo) {
    return (
      <Stack>
        <p className="dg-card__hint">{t('account.password.reset.hint')}</p>
        {send.isError && <ErrorState error={send.error} />}
        <Row>
          <Button busy={send.isPending} busyLabel={t('action.saving')} disabled={wait > 0} onClick={() => send.mutate()}>
            {t('auth.code.send')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
        <CodeWait seconds={wait} />
      </Stack>
    );
  }

  return (
    <form onSubmit={submit} noValidate>
      <Stack>
        <Notice tone="info">{t('account.password.reset.sent', { phone: formatPhone(sentTo) })}</Notice>
        {send.isError && <ErrorState error={send.error} />}
        <ResendTimer onSend={() => send.mutate()} send={send} startCounting />
        {reset.isError && <ErrorState error={reset.error} />}
        <CodeField value={code} onChange={setCode} error={codeError} />
        <NewPasswordFields
          labels={{
            password: t('account.password.new'),
            confirm: t('account.password.confirm'),
            mismatch: t('account.password.mismatch'),
          }}
          password={next}
          confirmation={confirmation}
          onPassword={setNext}
          onConfirmation={setConfirmation}
          mismatch={mismatch}
        />
        <Row>
          <Button type="submit" busy={reset.isPending} busyLabel={t('action.saving')}>
            {t('account.password.reset.submit')}
          </Button>
          <Button variant="ghost" onClick={onCancel}>{t('action.cancel')}</Button>
        </Row>
      </Stack>
    </form>
  );
}

function SessionsCard() {
  const api = useApi();
  const { t, formatDate } = useI18n();
  const { signOut } = useSession();

  const sessions = useQuery({ queryKey: ['account', 'sessions'], queryFn: () => api.listSessions() });

  const everywhere = useMutation({
    mutationFn: () => api.revokeAllSessions(),
    // This device's session was one of them, so it ends here too.
    onSuccess: () => signOut(),
  });

  return (
    <Card title={t('account.sessions')} hint={t('account.sessions.hint')}>
      <Stack>
        {sessions.isPending && <LoadingState />}
        {sessions.isError && <ErrorState error={sessions.error} onRetry={() => void sessions.refetch()} />}
        {sessions.data?.map((session) => (
          <Row key={session.id} between>
            <span className="dg-text-sm" dir="auto">{session.userAgent || t('account.sessions.unknownDevice')}</span>
            <span className="dg-muted dg-text-sm">{t('account.sessions.since', { date: formatDate(session.createdAt) })}</span>
          </Row>
        ))}
        {everywhere.isError && <ErrorState error={everywhere.error} />}
        <Row>
          <Button variant="danger" busy={everywhere.isPending} onClick={() => everywhere.mutate()}>
            {t('account.sessions.signOutAll')}
          </Button>
        </Row>
      </Stack>
    </Card>
  );
}

/** The account's own password, asked for before any change to how it signs in. */
function AccountPasswordField({ value, onChange }: { value: string; onChange: (value: string) => void }) {
  const { t } = useI18n();
  return (
    <CurrentPasswordField
      label={t('account.currentPassword')}
      hint={t('account.currentPassword.hint')}
      value={value}
      onChange={onChange}
    />
  );
}
